import { ReactElement, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Mutation_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  initialValuesFactory,
  KapGoalForm,
  KapGoalFormValues,
} from 'src/screens/kap/program/details/goals/edit/KapGoalForm'
import { insertKapGoalMutation } from 'src/screens/kap/program/details/goals/kapGoalQueries'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { CreateButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

export const KapGoalCreatePage = (): ReactElement => {
  const { programId } = useParams()
  const program_id = parseInt(programId as string)
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const { canModifyGoals, loading } = usePermissionsForKapProgram(program_id)
  const [initialValues, setInitialValues] = useState<KapGoalFormValues | null>(null)

  const onSubmit = () => {
    // trigger submit event
    document.getElementById('goal-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const handleOnSave = async (values: KapGoalFormValues) => {
    const insertObject = {
      name: values.name,
      description: HtmlSanitizer.sanitize(values.description),
      programId: values.programId,
      measuresToBeAdded: values.measureIds?.map((measureId) => ({
        kap_measure_id: measureId,
      })),
    }

    const { error } = await urqlClient
      .mutation<{
        insert_kap_goal: Mutation_Root['insert_kap_goal']
      }>(insertKapGoalMutation, insertObject)
      .toPromise()

    if (error) {
      notificationService.operationFailed()
    } else {
      setInitialValues(values)
      notificationService.changesSaved()
      onBack()
    }
  }

  useEffect(() => {
    const initializeFormValues = () => {
      const initialValues = initialValuesFactory(program_id)
      setInitialValues(initialValues)
    }

    initializeFormValues()
  }, [program_id])

  const onBack = () => {
    navigate(ROUTES.KapDetailsRoot.nested.Goals.params({ programId }))
  }

  if (!loading && !canModifyGoals) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.goal.title.add')}
      hasSecondLevelNavigation={false}
      onBack={onBack}
      actions={
        <>
          <CreateButton origin="header" onClick={onSubmit} />
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && initialValues && (
            <>
              <HelpAndInstructions labelKey="label.help.kap.program.goals" defaultExpansion />
              <KapGoalForm initialValues={initialValues} onSave={handleOnSave} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
